import React from 'react'
import {Document, Image, Page, StyleSheet, Text, View} from '@react-pdf/renderer'
import logo from '../../../../../_websoft/assets/images/logo.jpg'
import {IsporuceniPaketiModel, IsporuceniPaketiSumaModel} from '../../../../models/KreirajOtpremnicuModel'

type Props = {
    isporuceniPaketi: IsporuceniPaketiModel[]
    isporuceniPaketiSuma: IsporuceniPaketiSumaModel[]
    nalogIsporucen: boolean
    datum?: string
}

const styles = StyleSheet.create({
    page: {
        padding: 28,
        fontSize: 8,
        color: '#1f2933',
    },
    header: {
        borderBottom: '2px solid #1f2933',
        paddingBottom: 12,
        marginBottom: 18,
    },
    logoTitleWrap: {
        flexDirection: 'column',
        alignItems: 'flex-start',
    },
    logo: {
        width: 250,
        objectFit: 'contain',
        marginBottom: 8,
    },
    title: {
        fontSize: 20,
        marginTop: 10,
        fontWeight: 'bold',
        textTransform: 'uppercase',
    },
    subtitle: {
        marginTop: 4,
        fontSize: 9,
        color: '#5f6b7a',
    },
    infoGrid: {
        flexDirection: 'row',
        gap: 12,
        marginBottom: 16,
    },
    infoCard: {
        flex: 1,
        border: '1px solid #d8dde6',
        padding: 10,
    },
    compactInfoCard: {
        flex: 0.7,
    },
    splitInfoCard: {
        flex: 1.6,
        border: '1px solid #d8dde6',
        flexDirection: 'row',
    },
    splitInfoSection: {
        flex: 1,
        padding: 10,
    },
    splitInfoDivider: {
        borderRight: '1px solid #d8dde6',
    },
    sectionTitle: {
        fontSize: 10,
        fontWeight: 'bold',
        textTransform: 'uppercase',
        marginBottom: 6,
    },
    muted: {
        color: '#5f6b7a',
    },
    table: {
        border: '1px solid #1f2933',
        marginBottom: 16,
    },
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: '#1f2933',
        color: '#ffffff',
        fontWeight: 'bold',
    },
    tableRow: {
        flexDirection: 'row',
        borderTop: '1px solid #d8dde6',
    },
    tableTotalRow: {
        flexDirection: 'row',
        borderTop: '1px solid #1f2933',
        backgroundColor: '#f3f4f6',
        fontWeight: 'bold',
    },
    cell: {
        padding: 5,
        borderRight: '1px solid #d8dde6',
    },
    numberCell: {
        textAlign: 'right',
    },
    summary: {
        marginLeft: 'auto',
        width: 240,
        border: '1px solid #1f2933',
    },
    summaryRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        padding: 7,
        borderBottom: '1px solid #d8dde6',
    },
    signatureWrap: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginTop: 42,
    },
    signatureBox: {
        width: 180,
    },
    signatureDate: {
        marginBottom: 18,
        color: '#5f6b7a',
    },
    signature: {
        borderTop: '1px solid #1f2933',
        paddingTop: 6,
        textAlign: 'center',
    },
    pageNumber: {
        position: 'absolute',
        fontSize: 9,
        bottom: 16,
        left: 0,
        right: 28,
        textAlign: 'right',
        color: '#5f6b7a',
    },
})

const toNumber = (value?: string | number | null) => {
    const parsedValue = Number(String(value ?? '').replace(',', '.'))

    return Number.isFinite(parsedValue) ? parsedValue : 0
}

const formatNumber = (value: number) => value.toFixed(2)

const formatDate = (value?: string) => {
    if (!value) {
        return '-'
    }

    const [year, month, day] = value.split('-')

    return year && month && day ? `${day}.${month}.${year}` : value
}

const calculateSquareMeters = (paket: IsporuceniPaketiModel) =>
    (toNumber(paket.duzina) * toNumber(paket.sirinaPaketa) * toNumber(paket.brPanela)) / 1000000

const getNalogLabel = (paket?: IsporuceniPaketiModel | IsporuceniPaketiSumaModel) => {
    if (!paket?.proizvodnja) {
        return '-'
    }

    return `${paket.proizvodnja.brojNaloga}${paket.proizvodnja.stavka ? ` - ${paket.proizvodnja.stavka}` : ''}`
}

const getPanelLabel = (paket?: IsporuceniPaketiModel | IsporuceniPaketiSumaModel) =>
    paket?.proizvodnja?.tip_panela?.Naziv || '-'

const getBrPaketaMap = (paketi: IsporuceniPaketiModel[]) => {
    const groupedPaketi = paketi.reduce<Record<string, Set<string>>>((map, paket) => {
        const nalogKey = getNalogLabel(paket)

        if (!map[nalogKey]) {
            map[nalogKey] = new Set()
        }

        map[nalogKey].add(paket.nazivPanela)

        return map
    }, {})

    return Object.entries(groupedPaketi).reduce<Record<string, number>>((map, [nalogKey, paketiSet]) => {
        map[nalogKey] = paketiSet.size

        return map
    }, {})
}

const getUniqueNalozi = (paketi: IsporuceniPaketiModel[]) =>
    Array.from(new Set(paketi.map((paket) => getNalogLabel(paket)).filter(Boolean)))

const calculateSummaryTotals = (
    sums: IsporuceniPaketiSumaModel[],
    brPaketaMap: Record<string, number>
) =>
    sums.reduce(
        (totals, suma) => {
            const nalogKey = getNalogLabel(suma)
            const brPaketa = brPaketaMap[nalogKey] ?? 0
            const artikal = suma.proizvodnja?.artikal as unknown as { tezina?: string | number }
            const netto = toNumber(suma.suma) * toNumber(artikal?.tezina)
            const brutto = netto + 10 * brPaketa

            return {
                netto: totals.netto + netto,
                brutto: totals.brutto + brutto,
                brPaketa: totals.brPaketa + brPaketa,
            }
        },
        {netto: 0, brutto: 0, brPaketa: 0}
    )

const KreirajOtpremnicuPdf: React.FC<Props> = ({
                                                   isporuceniPaketi,
                                                   isporuceniPaketiSuma,
                                                   nalogIsporucen,
                                                   datum,
                                               }) => {
    const firstPaket = isporuceniPaketi[0]
    const brPaketaMap = getBrPaketaMap(isporuceniPaketi)
    const summaryTotals = calculateSummaryTotals(isporuceniPaketiSuma, brPaketaMap)
    const uniqueNalozi = getUniqueNalozi(isporuceniPaketi)
    const uniquePonude = Array.from(new Set(
        isporuceniPaketi.map((paket) => paket.proizvodnja?.brojPonude?.trim()).filter(Boolean)
    ))
    const ukupnoKvadrata = isporuceniPaketi.reduce(
        (sum, paket) => sum + calculateSquareMeters(paket),
        0
    )

    return (
        <Document title='otpremnica_paneli'>
            <Page size='A4' style={styles.page}>
                <View style={styles.header}>
                    <View style={styles.logoTitleWrap}>
                        <Image src={logo} style={styles.logo}/>
                        <Text style={styles.title}>Otpremnica</Text>
                        <Text style={styles.subtitle}>Paneli paketi</Text>
                    </View>
                </View>

                <View style={styles.infoGrid}>
                    <View style={styles.infoCard}>
                        <Text style={styles.sectionTitle}>Kupac</Text>
                        <Text>{firstPaket?.proizvodnja?.kupac || '-'}</Text>
                    </View>
                    <View style={[styles.infoCard, styles.compactInfoCard]}>
                        <Text style={styles.sectionTitle}>Nalog</Text>
                        <Text>{uniqueNalozi.length > 0 ? uniqueNalozi.join(', ') : '-'}</Text>
                    </View>
                    <View style={[styles.infoCard, styles.compactInfoCard]}>
                        <Text style={styles.sectionTitle}>Broj Ponude</Text>
                        <Text>{uniquePonude.length > 0 ? uniquePonude.join(', ') : '-'}</Text>
                    </View>
                    <View style={styles.splitInfoCard}>
                        <View style={[styles.splitInfoSection, styles.splitInfoDivider]}>
                            <Text style={styles.sectionTitle}>Status</Text>
                            <Text>{nalogIsporucen}</Text>
                        </View>
                        <View style={styles.splitInfoSection}>
                            <Text style={styles.sectionTitle}>Datum</Text>
                            <Text>{formatDate(datum)}</Text>
                        </View>
                    </View>
                </View>

                <View style={styles.table}>
                    <View style={styles.tableHeader}>
                        <Text style={[styles.cell, {width: '6%'}]}>#</Text>
                        <Text style={[styles.cell, {width: '16%'}]}>Nalog</Text>
                        <Text style={[styles.cell, {width: '14%'}]}>Panel</Text>
                        <Text style={[styles.cell, {width: '12%'}]}>Paket</Text>
                        <Text style={[styles.cell, styles.numberCell, {width: '12%'}]}>Duzina</Text>
                        <Text style={[styles.cell, styles.numberCell, {width: '11%'}]}>Sirina</Text>
                        <Text style={[styles.cell, styles.numberCell, {width: '9%'}]}>Kom.</Text>
                        <Text style={[styles.cell, styles.numberCell, {width: '9%'}]}>m2</Text>
                        <Text style={[styles.cell, {width: '11%', borderRight: 0}]}>Isporucio</Text>
                    </View>

                    {isporuceniPaketi.map((paket, index) => (
                        <View key={paket.id} style={styles.tableRow} wrap={false}>
                            <Text style={[styles.cell, {width: '6%'}]}>{index + 1}</Text>
                            <Text style={[styles.cell, {width: '16%'}]}>{getNalogLabel(paket)}</Text>
                            <Text style={[styles.cell, {width: '14%'}]}>{getPanelLabel(paket)}</Text>
                            <Text style={[styles.cell, {width: '12%'}]}>{paket.nazivPanela}</Text>
                            <Text style={[styles.cell, styles.numberCell, {width: '12%'}]}>
                                {formatNumber(toNumber(paket.duzina))}
                            </Text>
                            <Text style={[styles.cell, styles.numberCell, {width: '11%'}]}>
                                {paket.sirinaPaketa}
                            </Text>
                            <Text style={[styles.cell, styles.numberCell, {width: '9%'}]}>{paket.brPanela}</Text>
                            <Text style={[styles.cell, styles.numberCell, {width: '9%'}]}>
                                {formatNumber(calculateSquareMeters(paket))}
                            </Text>
                            <Text style={[styles.cell, {width: '11%', borderRight: 0}]}>
                                {paket.uposlenik?.UposlenikImePrezime || '-'}
                            </Text>
                        </View>
                    ))}
                </View>

                <View style={styles.table}>
                    <View style={styles.tableHeader}>
                        <Text style={[styles.cell, {width: '22%'}]}>Nalog</Text>
                        <Text style={[styles.cell, {width: '18%'}]}>Panel</Text>
                        <Text style={[styles.cell, styles.numberCell, {width: '15%'}]}>Kvadratura</Text>
                        <Text style={[styles.cell, styles.numberCell, {width: '15%'}]}>Netto</Text>
                        <Text style={[styles.cell, styles.numberCell, {width: '15%'}]}>Brutto</Text>
                        <Text style={[styles.cell, styles.numberCell, {width: '15%', borderRight: 0}]}>Br. paketa</Text>
                    </View>

                    {isporuceniPaketiSuma.map((suma) => {
                        const nalogKey = getNalogLabel(suma)
                        const brPaketa = brPaketaMap[nalogKey] ?? 0
                        const artikal = suma.proizvodnja?.artikal as unknown as { tezina?: string | number }
                        const tezina = toNumber(artikal?.tezina)
                        const netto = toNumber(suma.suma) * tezina
                        const brutto = netto + 10 * brPaketa

                        return (
                            <View key={`${nalogKey}-${suma.sirinaPaketa}`} style={styles.tableRow} wrap={false}>
                                <Text style={[styles.cell, {width: '22%'}]}>{nalogKey}</Text>
                                <Text style={[styles.cell, {width: '18%'}]}>{getPanelLabel(suma)}</Text>
                                <Text style={[styles.cell, styles.numberCell, {width: '15%'}]}>
                                    {formatNumber(toNumber(suma.suma))}
                                </Text>
                                <Text style={[styles.cell, styles.numberCell, {width: '15%'}]}>
                                    {formatNumber(netto)}
                                </Text>
                                <Text style={[styles.cell, styles.numberCell, {width: '15%'}]}>
                                    {formatNumber(brutto)}
                                </Text>
                                <Text style={[styles.cell, styles.numberCell, {width: '15%', borderRight: 0}]}>
                                    {brPaketa}
                                </Text>
                            </View>
                        )
                    })}
                    <View style={styles.tableTotalRow} wrap={false}>
                        <Text style={[styles.cell, {width: '22%'}]}>Ukupno</Text>
                        <Text style={[styles.cell, {width: '18%'}]}></Text>
                        <Text style={[styles.cell, styles.numberCell, {width: '15%'}]}>
                            {formatNumber(ukupnoKvadrata)} m2
                        </Text>
                        <Text style={[styles.cell, styles.numberCell, {width: '15%'}]}>
                            {formatNumber(summaryTotals.netto)}
                        </Text>
                        <Text style={[styles.cell, styles.numberCell, {width: '15%'}]}>
                            {formatNumber(summaryTotals.brutto)}
                        </Text>
                        <Text style={[styles.cell, styles.numberCell, {width: '15%', borderRight: 0}]}>
                            {summaryTotals.brPaketa}
                        </Text>
                    </View>
                </View>

                <View style={styles.signatureWrap}>
                    <View style={styles.signatureBox}>
                        <Text style={styles.signature}>Otpremio</Text>
                    </View>
                    <View style={styles.signatureBox}>
                        <Text style={styles.signature}>Preuzeo</Text>
                    </View>
                </View>

                <Text
                    style={styles.pageNumber}
                    render={({pageNumber, totalPages}) => `${pageNumber} / ${totalPages}`}
                    fixed
                />
            </Page>
        </Document>
    )
}

export default KreirajOtpremnicuPdf
